/* ==========================================================================
   LEGIT — "Build a Stack" nudge inside the Rebuy Smart Cart
   --------------------------------------------------------------------------
   INSTALL — theme asset. Load once from layout/theme.liquid, just above </body>:
       <script src="{{ 'legit-rebuy-stack.js' | asset_url }}" defer="defer"></script>
   The box's styling lives in the Rebuy Custom CSS file (legit-rebuy-cart.css,
   §14) scoped under #rebuy-cart — this file only handles logic.

   WHAT IT DOES
   When the cart holds >= CONFIG.minItems *bundle-eligible* items, it injects a
   dark "Build a Stack & Save" box into the Rebuy drawer. Clicking it:
     1. sends those items into Loop's bundle builder, pre-filled, via Loop's
        SUPPORTED url params: ?selectedVariants=<ids>&selectedQuantities=<qtys>
        (help.loopwork.co/en/articles/12741075-build-your-bundle)
     2. removes exactly those items from the cart in the same action, so the
        shopper doesn't end up with the singles AND the bundle. Items that
        aren't eligible are left untouched.

   ELIGIBILITY  ← read this before changing anything
   "Eligible" = the product offers a subscription (its /products/<handle>.js has
   selling_plan_groups). This assumes EVERY Subscribe-&-Save product is part of
   the Build Your Stack bundle. That holds for Legit today. If you ever have a
   subscribable product that is NOT in the bundle, this would remove it from the
   cart without Loop pre-filling it (i.e. lose it) — so in that case switch
   eligibility to an explicit allow-list: set CONFIG.eligibleVariantIds to the
   bundle's variant IDs and the code uses those instead of the selling-plan probe.

   SAFETY — Rebuy is a Vue app; re-renders wipe injected nodes. The observer is
   DISCONNECTED before we touch the DOM and reconnected after (same pattern as
   the Loop files). Eligibility is async (a fetch per product handle, cached), so
   it's computed off to the side and the DOM injection stays synchronous.
   ========================================================================== */
(function () {
  'use strict';

  var CONFIG = {
    bundleUrl: '/a/loop_subscriptions/bundle/3764ab07abb741259f544fad727e3134',
    minItems: 3,                       // Loop's stack discount starts at 3 items

    /* Leave null to use the "has a selling plan" probe (default). To pin
       eligibility to an explicit set instead, put the bundle's variant IDs here
       (numbers) and the probe is skipped. */
    eligibleVariantIds: null,

    copy: {
      eyebrow: 'Stack &amp; Save',
      title: 'Turn your cart into a stack',
      /* {n} = number of eligible items currently in the cart */
      text: 'You’ve got <b>{n}</b> stackable items. Build them into a bundle and save up to <b>18%</b> on one time purchases & subscribe for <b>up to 26% off!</b>',
      cta: 'Build your stack'
    }
  };

  /* -------- self-contained styling ---------------------------------------
     The box is injected by JS, so its look must NOT depend on the Rebuy Custom
     CSS field reaching it (it may land just outside #rebuy-cart, and the field
     can truncate). Literal colour values + the THEME's font vars (defined on
     :root site-wide, so they resolve anywhere). Class is unique → no leak, no
     need for an #rebuy-cart prefix. §14 in legit-rebuy-cart.css is now redundant
     but harmless. */
  var STYLE =
    ".legit-cart-stack{position:relative;overflow:hidden;background:#17181a;border-radius:9px;padding:16px 18px;margin:4px 0 12px;font-family:var(--FONT-STACK-BODY,'Roboto',-apple-system,sans-serif)}" +
    ".legit-cart-stack::after{content:'';position:absolute;left:0;right:0;bottom:0;height:3px;background:linear-gradient(90deg,#f0483e,#fcb525,#acd588,#48c1bb,#f0483e)}" +
    ".legit-cart-stack__eyebrow{display:inline-flex;align-items:center;gap:9px;font-weight:700;font-size:10px;letter-spacing:.16em;text-transform:uppercase;color:#9aa0a4}" +
    ".legit-cart-stack__eyebrow::before{content:'';width:18px;height:2px;background:#f0483e}" +
    ".legit-cart-stack__title{margin:8px 0 0;color:#fff;font-family:var(--FONT-STACK-HEADING,'Roboto',-apple-system,sans-serif);font-weight:900;font-size:16px;line-height:1.2;letter-spacing:-.01em;text-transform:uppercase}" +
    ".legit-cart-stack__text{margin:6px 0 12px;color:#b9bdc2;font-size:12.5px;line-height:1.5}" +
    ".legit-cart-stack__text b{color:#acd588;font-weight:800}" +
    ".legit-cart-stack__btn{display:inline-flex;align-items:center;gap:8px;border:1.5px solid #acd588;border-radius:9px;background:transparent;padding:10px 16px;color:#acd588;font-weight:800;font-size:11px;letter-spacing:.08em;text-transform:uppercase;cursor:pointer;transition:background .15s ease,color .15s ease}" +
    ".legit-cart-stack__btn:hover{background:#acd588;color:#17181a}";

  function injectStyle() {
    if (document.getElementById('legit-cart-stack-style')) return;
    var s = document.createElement('style');
    s.id = 'legit-cart-stack-style';
    s.textContent = STYLE;
    (document.head || document.documentElement).appendChild(s);
  }

  /* -------- eligibility ---------------------------------------------------- */
  var probeCache = {};                 // handle -> Promise<bool>

  function productHasPlan(handle) {
    if (!handle) return Promise.resolve(false);
    if (probeCache[handle]) return probeCache[handle];
    return (probeCache[handle] = fetch('/products/' + encodeURIComponent(handle) + '.js', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (p) { return !!(p && p.selling_plan_groups && p.selling_plan_groups.length); })
      .catch(function () { return false; }));
  }

  function itemEligible(it) {
    if (CONFIG.eligibleVariantIds) {
      return Promise.resolve(CONFIG.eligibleVariantIds.indexOf(variantId(it)) > -1);
    }
    return productHasPlan(handleOf(it));
  }

  /* -------- Rebuy cart accessors ------------------------------------------ */
  function items() {
    try {
      var c = window.Rebuy && window.Rebuy.Cart && window.Rebuy.Cart.cart;
      return (c && Array.isArray(c.items)) ? c.items : [];
    } catch (e) { return []; }
  }
  function variantId(it) {
    return it.variant_id || (it.variant && it.variant.id) || it.id || null;
  }
  function handleOf(it) {
    return it.handle || (it.product && it.product.handle) || '';
  }

  /* Suppress the nudge when the cart ALREADY holds a Loop bundle or any
     subscription line — no point nudging someone who's already stacking or
     subscribing. Read straight off the line items, so it's synchronous.
     If this ever fails to trigger, log Rebuy.Cart.cart.items[0] and check which
     field carries the bundle/subscription flag on your store, then add it here. */
  function cartHasBundleOrSubscription() {
    return items().some(function (it) {
      /* subscription line: Shopify sets selling_plan_allocation on a subscribed
         line item; keep the looser fallbacks for safety across Rebuy shapes */
      if (it.selling_plan_allocation || it.selling_plan || it.subscription) return true;

      /* Loop bundle line: carries a bundle property (shows as
         "bundleName: BUILD YOUR STACK" in the cart). Match any property key that
         contains "bundle", in either the object or [{name,value}] shape. */
      var p = it.properties;
      if (p && typeof p === 'object') {
        if (Array.isArray(p)) {
          for (var i = 0; i < p.length; i++) {
            if (p[i] && /bundle/i.test(String(p[i].name || p[i].key || ''))) return true;
          }
        } else {
          for (var k in p) { if (/bundle/i.test(k)) return true; }
        }
      }
      return !!(it.bundle_id || it._bundle_id || it.bundle);
    });
  }

  /* resolve the eligible subset of the current cart (async) */
  function eligibleItems() {
    var list = items();
    if (!list.length) return Promise.resolve([]);
    return Promise.all(list.map(function (it) {
      return itemEligible(it).then(function (ok) { return ok ? it : null; });
    })).then(function (a) { return a.filter(Boolean); });
  }

  /* -------- the action: pre-fill builder + remove those items + go -------- */
  var busy = false;
  function go() {
    if (busy) return;
    busy = true;
    eligibleItems().then(function (elig) {
      if (elig.length < CONFIG.minItems) { busy = false; return; }   // guard
      var ids = elig.map(variantId).filter(Boolean);
      var qty = elig.map(function (it) { return it.quantity || 1; });
      var url = CONFIG.bundleUrl +
        '?selectedVariants=' + ids.join(',') +
        '&selectedQuantities=' + qty.join(',');

      var updates = {};
      ids.forEach(function (v) { updates[v] = 0; });               // remove = qty 0
      fetch('/cart/update.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates: updates })
      }).then(function () { window.location.href = url; })
        .catch(function () { window.location.href = url; });        // navigate regardless
    });
  }

  /* -------- the box ------------------------------------------------------- */
  function buildBox(count) {
    var el = document.createElement('div');
    el.className = 'legit-cart-stack';
    el.setAttribute('data-legit-cart-stack', '');
    el.innerHTML =
      '<span class="legit-cart-stack__eyebrow">' + CONFIG.copy.eyebrow + '</span>' +
      '<p class="legit-cart-stack__title">' + CONFIG.copy.title + '</p>' +
      '<p class="legit-cart-stack__text">' + CONFIG.copy.text.replace('{n}', count) + '</p>' +
      '<button type="button" class="legit-cart-stack__btn" data-legit-cart-stack-go>' +
        CONFIG.copy.cta + ' →</button>';
    el.querySelector('[data-legit-cart-stack-go]').addEventListener('click', go);
    return el;
  }

  /* -------- render (synchronous, observer-safe) --------------------------- */
  var eligibleCount = 0;
  var observer = null, queued = false;

  function render() {
    queued = false;
    if (observer) observer.disconnect();
    try {
      /* mount just above the cross-sell widget (right after the items); fall
         back to just above the subtotal. Both are Rebuy component wrappers. */
      var host = document.querySelector('[data-rebuy-component="cross-sell-widget"]') ||
                 document.querySelector('[data-rebuy-component="cart-subtotal"]');
      var box = document.querySelector('[data-legit-cart-stack]');
      var show = eligibleCount >= CONFIG.minItems && !!host && !cartHasBundleOrSubscription();

      if (show) {
        if (!box) {
          host.parentNode.insertBefore(buildBox(eligibleCount), host);
        } else {
          /* keep the count fresh without rebuilding the node */
          var t = box.querySelector('.legit-cart-stack__text');
          if (t) t.innerHTML = CONFIG.copy.text.replace('{n}', eligibleCount);
        }
      } else if (box) {
        box.remove();
      }
    } catch (e) {
      if (window.console) console.warn('[legit-rebuy-stack]', e);
    } finally {
      if (observer) observer.observe(document.body, { childList: true, subtree: true });
    }
  }
  function schedule() { if (!queued) { queued = true; window.requestAnimationFrame(render); } }

  /* recompute eligibility (async) then re-render */
  function recompute() {
    eligibleItems().then(function (elig) { eligibleCount = elig.length; schedule(); });
  }

  /* -------- start --------------------------------------------------------- */
  function start() {
    injectStyle();
    observer = new MutationObserver(schedule);
    observer.observe(document.body, { childList: true, subtree: true });

    /* Rebuy cart lifecycle → recompute the eligible count */
    ['rebuy:cart.ready', 'rebuy:cart.change', 'rebuy:cart.enriched', 'rebuy:smartcart.ready', 'rebuy:smartcart.show']
      .forEach(function (ev) { document.addEventListener(ev, recompute); });

    recompute();
    setTimeout(recompute, 1200);       // catch a late Rebuy mount
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
